import React from "react";
import { CARD_TITLE } from "../utils/styles";

/* ─── Icon components ─── */

/* < 15 sec → X-circle  (dropped / rejected call) */
const IconPhone = ({ color }) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

/* 15s–1min → Microphone  (intro pitch / speaking) */
const IconChat = ({ color }) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);

/* 1–3 min → Activity wave  (active engagement / conversation flow) */
const IconPeople = ({ color }) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

/* > 3 min → Star  (high intent / high value) */
const IconTrophy = ({ color }) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8.21 13.89L7 23L12 20L17 23L15.79 13.88" />
    <circle cx="12" cy="8" r="7" />
    <polygon points="12 5.5 13 7.5 15.2 7.8 13.6 9.3 14 11.5 12 10.5 10 11.5 10.4 9.3 8.8 7.8 11 7.5" fill={color} stroke="none" />
  </svg>
);

const BUCKETS = [
  {
    key: "below15Sec",
    label: "< 15 sec",
    description: "Immediate drop",
    color: "#EF4444",
    bgColor: "#FFF5F5",
    trackColor: "#FEE2E2",
    borderColor: "#FCA5A5", // Darker border color
    Icon: IconPhone,
  },
  {
    key: "between15To60",
    label: "15s – 1 min",
    description: "Intro pitch",
    color: "#F59E0B",
    bgColor: "#FFFBEB",
    trackColor: "#FEF3C7",
    borderColor: "#FCD34D", // Darker border color
    Icon: IconChat,
  },
  {
    key: "between1To3Min",
    label: "1 – 3 min",
    description: "Active engagement",
    color: "#10B981",
    bgColor: "#F0FDF4",
    trackColor: "#D1FAE5",
    borderColor: "#86EFAC", // Darker border color
    Icon: IconPeople,
  },
  {
    key: "above3Min",
    label: "> 3 min",
    description: "High intent",
    color: "#7C3AED",
    bgColor: "#F9F5FF",
    trackColor: "#EDE9FE",
    borderColor: "#C084FC", // Darker border color
    Icon: IconTrophy,
  },
];


/* ─── Skeleton ─── */
const SkeletonCard = () => (
  <div
    style={{
      flex: "1 1 170px",
      minWidth: 0,
      padding: "16px 20px",
      borderRadius: 10,
      border: "1px solid #F1F1F4",
      background: "#FAFAFA",
      display: "flex",
      flexDirection: "column",
    }}
  >
    {/* Top: icon + label/desc skeleton */}
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
      <div className="skeleton-pulse" style={{ width: 30, height: 30, borderRadius: "50%", flexShrink: 0 }} />
      <div style={{ flex: 1, height: 32, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <div className="skeleton-pulse" style={{ height: 11, width: "60%", borderRadius: 4 }} />
        <div className="skeleton-pulse" style={{ height: 9, width: "80%", borderRadius: 4, marginTop: 4 }} />
      </div>
    </div>
    {/* Middle: count skeleton */}
    <div className="skeleton-pulse" style={{ height: 20, width: "45%", borderRadius: 4 }} />
    <div className="skeleton-pulse" style={{ height: 9, width: "30%", borderRadius: 4, marginTop: 6, marginBottom: 16 }} />
    {/* Bottom container skeleton */}
    <div style={{ marginTop: "auto" }}>
      <div className="skeleton-pulse" style={{ height: 4, borderRadius: 99, width: "100%" }} />
      <div className="skeleton-pulse" style={{ height: 10, width: "35%", borderRadius: 4, marginTop: 8, marginLeft: "auto" }} />
    </div>
  </div>
);

/* ─── Main component ─── */
const CallDurationCards = ({ stats, loading }) => {
  /* ── Loading ── */
  if (loading) {
    return (
      <div
        className="card card-custom bg-white"
        style={{ borderRadius: 12, border: "1px solid #F1F1F4", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", padding: "20px 24px", marginBottom: 16 }}
      >
        <div style={{ marginBottom: 12 }}>
          <div className="skeleton-pulse" style={{ height: 11, width: 160, borderRadius: 4, marginBottom: 6 }} />
          <div className="skeleton-pulse" style={{ height: 9, width: 240, borderRadius: 4 }} />
        </div>
        <div style={{ display: "flex", gap: 20, alignItems: "stretch" }}>
          {[0, 1, 2, 3].map((i) => <SkeletonCard key={i} />)}
          <div style={{ width: 160, flexShrink: 0, display: "flex", flexDirection: "column", gap: 10 }}>
            {[0, 1].map((i) => (
              <div
                key={i}
                style={{
                  flex: "1 1 0px",
                  border: "1px solid #F1F1F4",
                  borderRadius: 8,
                  background: "#FAFAFA",
                  padding: "10px 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <div className="skeleton-pulse" style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div className="skeleton-pulse" style={{ height: 8, width: "70%", borderRadius: 4, marginBottom: 4 }} />
                  <div className="skeleton-pulse" style={{ height: 13, width: "50%", borderRadius: 4 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* ── Data ── */
  const durationData = stats?.callDurationAnalytics || {};
  const totalCount = BUCKETS.reduce((sum, b) => sum + (durationData[b.key]?.count || 0), 0);
  const hasData = totalCount > 0;

  const getAvgDuration = () => {
    if (!hasData) return "00:00";
    const b1 = durationData.below15Sec?.count || 0;
    const b2 = durationData.between15To60?.count || 0;
    const b3 = durationData.between1To3Min?.count || 0;
    const b4 = durationData.above3Min?.count || 0;
    const total = b1 + b2 + b3 + b4;
    if (!total) return "00:00";
    const secs = Math.round(((b1 * 7.5) + (b2 * 37.5) + (b3 * 120) + (b4 * 240)) / total);
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  /* ── Render ── */
  return (
    <div
      className="card card-custom bg-white"
      style={{ borderRadius: 12, border: "1px solid #D4D4D8", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", padding: "20px 24px", marginBottom: 16 }}
    >
      {/* Header */}
      <div className="card-header border-0 p-0 mb-5">
        <h3
          className="card-title font-weight-bolder text-dark font-size-h5 m-0"
          style={CARD_TITLE}
        >
          Call Duration Analysis
        </h3>
      </div>

      {/* Cards row */}
      <div style={{ display: "flex", gap: 20, alignItems: "stretch", flexWrap: "wrap" }}>

        {/* 4 individual duration cards */}
        {BUCKETS.map((b) => {
          const item = durationData[b.key] || {};
          const count = item.count || 0;
          const pct = item.percentage || 0;
          const barW = Math.min(Math.max(pct, 0), 100);
          const { Icon } = b;

          // Muted colors if there is no data
          const cardBorderColor = hasData ? b.borderColor : "#E4E6EF";
          const cardIconBgColor = hasData ? b.bgColor : "#F3F6F9";
          const cardColor = hasData ? b.color : "#A1A5B7";
          const cardTrackColor = hasData ? b.trackColor : "#F1F1F4";

          return (
            <div
              key={b.key}
              style={{
                flex: "1 1 170px",
                minWidth: 0,
                padding: "16px 20px",
                borderRadius: 10,
                border: `1px solid ${cardBorderColor}`,
                background: "#FFFFFF",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
              }}
            >
              {/* Top: icon + label/desc (fixed height) */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                {/* Icon circle */}
                <div
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    backgroundColor: cardIconBgColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon color={cardColor} />
                </div>
                {/* Label + description with fixed height */}
                <div style={{ minWidth: 0, height: 32, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "#181C32", lineHeight: 1.1, whiteSpace: "nowrap" }}>
                    {b.label}
                  </div>
                  <div style={{ fontSize: "0.72rem", color: "#7E8299", fontWeight: 500, marginTop: 2, lineHeight: 1.1, whiteSpace: "nowrap" }}>
                    {b.description}
                  </div>
                </div>
              </div>

              {/* Middle: count */}
              <div>
                <span style={{ fontSize: "1.6rem", fontWeight: "700", color: hasData ? "#181C32" : "#7E8299", lineHeight: 1, letterSpacing: "-0.5px" }}>
                  {count.toLocaleString()}
                </span>
              </div>
              <div style={{ fontSize: "0.72rem", color: "#B5B5C3", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 4, marginBottom: 16 }}>
                calls
              </div>

              {/* Bottom: Progress bar and percentage (dedicated bottom container) */}
              <div style={{ marginTop: "auto" }}>
                {/* Progress bar */}
                <div style={{ height: 4, borderRadius: 99, background: cardTrackColor, overflow: "hidden", marginBottom: 8 }}>
                  <div
                    style={{
                      height: "100%",
                      width: `${barW}%`,
                      background: cardColor,
                      borderRadius: 99,
                      transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)",
                    }}
                  />
                </div>

                {/* Percentage */}
                <div style={{ textAlign: "right" }}>
                  <span style={{ fontSize: "0.95rem", fontWeight: 700, color: cardColor }}>
                    {pct.toFixed(2)}%
                  </span>
                </div>
              </div>
            </div>
          );
        })}

        {/* Right summary panel - Separate cards stacked vertically */}
        <div
          style={{
            width: 240,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {/* Average Call Duration Card */}
          <div
            style={{
              flex: "1 1 0px",
              border: "1px solid #E4E6EF",
              borderRadius: 12,
              background: "#FFFFFF",
              padding: "16px 20px",
              display: "flex",
              alignItems: "center",
              gap: 14,
              boxShadow: "0 10px 30px rgba(82, 63, 105, 0.03)",
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: hasData ? "#EFEBFC" : "#F3F6F9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={hasData ? "#7C3AED" : "#B5B5C3"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div>
              <div style={{ fontSize: "0.72rem", color: "#80808F", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.4px", lineHeight: 1.2 }}>
                Average Call Duration
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 4 }}>
                <span style={{ fontSize: "1.5rem", fontWeight: "800", color: hasData ? "#181C32" : "#7E8299", lineHeight: 1 }}>
                  {getAvgDuration()}
                </span>
                <span style={{ fontSize: "0.72rem", color: "#5E6278", fontWeight: 600 }}>mm:ss</span>
              </div>
            </div>
          </div>

          {/* Total Calls Card */}
          <div
            style={{
              flex: "1 1 0px",
              border: "1px solid #E4E6EF",
              borderRadius: 12,
              background: "#FFFFFF",
              padding: "16px 20px",
              display: "flex",
              alignItems: "center",
              gap: 14,
              boxShadow: "0 10px 30px rgba(82, 63, 105, 0.03)",
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: hasData ? "#E0F2FE" : "#F3F6F9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={hasData ? "#0ea5e9" : "#B5B5C3"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
            </div>
            <div>
              <div style={{ fontSize: "0.72rem", color: "#80808F", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.4px", lineHeight: 1.2 }}>
                Total Calls
              </div>
              <div style={{ fontSize: "1.5rem", fontWeight: "800", color: hasData ? "#181C32" : "#7E8299", lineHeight: 1, marginTop: 4 }}>
                {totalCount.toLocaleString()}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default React.memo(CallDurationCards);
